import type { FC, ReactNode } from 'react'

import { Minus, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { MAX_LINE_QUANTITY } from '@/lib/cart'
import { Button } from '@/components/ui/button'

interface QuantityStepperProps {
    value: number
    onChange: (value: number) => void
    label: string
    min?: number
    max?: number
    size?: 'sm' | 'default'
    className?: string
}

const QuantityStepper: FC<QuantityStepperProps> = ({
    value,
    onChange,
    label,
    min = 1,
    max = MAX_LINE_QUANTITY,
    size = 'default',
    className
}): ReactNode => {
    const buttonSize = size === 'sm' ? 'icon-sm' : 'icon'

    return (
        <div
            role='group'
            aria-label={`Quantity for ${label}`}
            className={cn('inline-flex items-center rounded-full border border-border bg-card p-0.5', className)}
        >
            <Button
                type='button'
                variant='ghost'
                size={buttonSize}
                disabled={value <= min}
                onClick={() => onChange(Math.max(min, value - 1))}
                aria-label={`Remove one ${label}`}
            >
                <Minus />
            </Button>

            <output
                aria-live='polite'
                className={cn('text-center font-semibold tabular-nums', size === 'sm' ? 'w-7 text-sm' : 'w-9 text-base')}
            >
                {value}
            </output>

            <Button
                type='button'
                variant='ghost'
                size={buttonSize}
                disabled={value >= max}
                onClick={() => onChange(Math.min(max, value + 1))}
                aria-label={`Add one more ${label}`}
            >
                <Plus />
            </Button>
        </div>
    )
}

export default QuantityStepper
